/**
 * أدوات موحّدة للتعامل مع المبالغ المالية في لوحة التحكم.
 *
 * سبب وجود هذا الملف: الحسابات المالية كانت تُجرى مباشرة بأرقام JavaScript
 * العشرية (IEEE-754)، فتظهر نتائج مثل 7.5900000000000185 بدل 7.59.
 * الدوال هنا تضمن أن كل مبلغ يُعرض برقمين عشريين وأن الجمع والطرح لا
 * يتراكم فيهما خطأ التقريب.
 *
 * ملاحظة مهمة: هذا علاج على مستوى العرض. الحل الجذري أن يرسل الـ backend
 * المبالغ محسوبة ومقرّبة مسبقاً — راجع docs/backend-money-formatting.md
 */

/** عدد الخانات العشرية المعتمد للمبالغ */
export const MONEY_DECIMALS = 2;

/**
 * يحوّل أي قيمة قادمة من الـ API إلى رقم.
 * يتعامل مع الأرقام، والنصوص ("372.6")، والنصوص التي تحمل عملة ("SAR 372.6").
 * يرجع 0 عند تعذّر التحويل.
 */
export const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined || value === "") return 0;

  const match = String(value).match(/-?\d+(\.\d+)?/);
  if (!match) return 0;

  const parsed = parseFloat(match[0]);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * يقرّب رقماً إلى خانتين عشريتين ويرجعه كرقم.
 * يُستخدم بعد كل عملية حسابية لمنع تراكم خطأ العشريات.
 */
export const roundMoney = (value, decimals = MONEY_DECIMALS) => {
  const num = toNumber(value);
  const factor = 10 ** decimals;
  // Number.EPSILON يعالج حالات مثل 1.005 التي تُقرَّب خطأً بدونه
  return Math.round((num + Number.EPSILON) * factor) / factor;
};

/**
 * يجمع عدة مبالغ ويقرّب الناتج مرة واحدة في النهاية.
 * مثال: sumMoney(360, 0.5, 0.08) => 360.58
 */
export const sumMoney = (...values) =>
  roundMoney(values.reduce((total, value) => total + toNumber(value), 0));

/**
 * يطرح بقية القيم من الأولى ويقرّب الناتج.
 * مثال: subtractMoney(368.31, 360.58, 0.14) => 7.59
 */
export const subtractMoney = (base, ...values) =>
  roundMoney(
    values.reduce((total, value) => total - toNumber(value), toNumber(base)),
  );

/**
 * ينسّق مبلغاً للعرض: خانتان عشريتان وفاصل آلاف.
 * يرجع placeholder (افتراضياً "-") إذا كانت القيمة فارغة أو غير رقمية.
 *
 * formatMoney(7.5900000000000185) => "7.59"
 * formatMoney("SAR 372.6")        => "372.60"
 * formatMoney(null)               => "-"
 */
export const formatMoney = (value, { placeholder = "-", currency } = {}) => {
  if (value === null || value === undefined || value === "") return placeholder;

  const num = toNumber(value);
  if (!Number.isFinite(num)) return placeholder;

  const text = roundMoney(num).toLocaleString("en-US", {
    minimumFractionDigits: MONEY_DECIMALS,
    maximumFractionDigits: MONEY_DECIMALS,
  });

  return currency ? `${text} ${currency}` : text;
};

/**
 * مثل formatMoney لكنه يرجع placeholder عندما تكون القيمة صفراً أيضاً.
 * مفيد للأعمدة التي لا معنى لعرض صفر فيها (حوالة لم تُنفَّذ بعد مثلاً).
 */
export const formatMoneyOrDash = (value, options = {}) => {
  if (toNumber(value) === 0) return options.placeholder ?? "-";
  return formatMoney(value, options);
};

/**
 * لعرض القيم كما ترد من الـ API مع توحيد الخانات العشرية.
 * يحافظ على رمز العملة إن كان مرفقاً بالقيمة.
 *
 * formatAmount("SAR 372.6") => "SAR 372.60"
 * formatAmount(372.6)       => "372.60"
 * formatAmount("-")         => "-"
 */
export const formatAmount = (value, { placeholder = "-" } = {}) => {
  if (value === null || value === undefined || value === "") return placeholder;

  const text = String(value).trim();

  // لا يحتوي على أي رقم — أعده كما هو (مثل "-" أو "غير متوفر")
  if (!/\d/.test(text)) return text || placeholder;

  const formatted = formatMoney(text, { placeholder });
  if (formatted === placeholder) return placeholder;

  // استخرج رمز العملة إن وُجد (أحرف لاتينية أو عربية حول الرقم)
  const currency = text.replace(/-?[\d,.\s]+/g, "").trim();
  return currency ? `${currency} ${formatted}` : formatted;
};
